import React, { useRef, useState, useEffect } from 'react';
import { useSnackbar } from "../context/SnackbarContext";


const UploadModal = ({ show, onClose, onImageSelect, onProcessing, onError, camera, gallery, t, detect = false }) => {
    const cameraInputRef = useRef(null);
    const galleryInputRef = useRef(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [opencvReady, setOpencvReady] = useState(false);
    const isSmallScreen = window.innerWidth <= 375;
    const [deviceCapabilities, setDeviceCapabilities] = useState({
        hasCamera: true,
        hasFileAccess: true
    });
    const { showSnackbar } = useSnackbar();
    const [alertModal, setAlertModal] = useState({
        show: false,
        message: '',
        title: 'Alert'
    });

    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /Android/i.test(ua);

    const isMobile = () => {
        return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    };

    const showAlert = (message, title = 'Alert') => {
        if (isMobile()) {
            setAlertModal({ show: true, message, title });
        } else {
            setAlertModal({ show: true, message, title });
        }
    };

    useEffect(() => {
        if (show) {
            setIsAnimating(true);
            setIsProcessing(false); // Reset processing state when modal opens
        } else {
            setIsAnimating(false);
        }
    }, [show]);

    useEffect(() => {
        loadOpenCV();
        checkDeviceCapabilities();
    }, []);

    const loadOpenCV = () => {
        if (window.cv && window.cv.CascadeClassifier) {
            setOpencvReady(true);
            console.log('OpenCV already loaded');
            return;
        }

        const existingScript = document.querySelector('script[src*="opencv.js"]');
        if (existingScript) {
            console.log('OpenCV script already exists, waiting for it to load...');
            const checkOpenCV = setInterval(() => {
                if (window.cv && window.cv.CascadeClassifier) {
                    clearInterval(checkOpenCV);
                    setOpencvReady(true);
                    console.log('OpenCV loaded successfully (existing script)');
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkOpenCV);
                if (!window.cv || !window.cv.CascadeClassifier) {
                    console.error('OpenCV failed to load after timeout');
                }
            }, 30000);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://docs.opencv.org/4.5.2/opencv.js';
        script.async = true;
        script.id = 'opencv-script';

        script.onload = () => {
            console.log('OpenCV script loaded, waiting for initialization...');
            const checkOpenCV = setInterval(() => {
                if (window.cv && window.cv.CascadeClassifier) {
                    clearInterval(checkOpenCV);
                    setOpencvReady(true);
                    console.log('OpenCV initialized successfully');
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkOpenCV);
                if (!window.cv || !window.cv.CascadeClassifier) {
                    console.error('OpenCV failed to initialize after timeout');
                }
            }, 30000);
        };

        script.onerror = () => {
            console.error('Failed to load OpenCV script');
        };

        document.body.appendChild(script);
    };

    const checkDeviceCapabilities = async () => {
        const capabilities = {
            hasCamera: false,
            hasFileAccess: true
        };

        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                capabilities.hasCamera = devices.some(device => device.kind === 'videoinput');
            } catch (error) {
                console.log('Could not enumerate devices:', error);
                capabilities.hasCamera = false;
            }
        }

        const input = document.createElement('input');
        input.type = 'file';
        capabilities.hasFileAccess = input.type === 'file';

        setDeviceCapabilities(capabilities);
    };

    const checkAndRequestCameraPermission = async () => {
        let initialPermissionState = 'prompt';

        try {
            console.log('Checking camera permission...');

            // Check permission state BEFORE requesting
            if (navigator.permissions) {
                try {
                    const permissionStatus = await navigator.permissions.query({ name: 'camera' });
                    initialPermissionState = permissionStatus.state;
                } catch (permError) {
                    console.log('Permission query not supported, assuming prompt will show');
                }
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: true
            });

            console.log('Camera permission granted');

            stream.getTracks().forEach(track => track.stop());

            return true;

        } catch (err) {
            console.error("Camera permission denied or error:", err);

            if (err.name === 'NotAllowedError') {
                // Check current permission state
                if (navigator.permissions) {
                    try {
                        const permissionStatus = await navigator.permissions.query({ name: 'camera' });

                        if (permissionStatus.state === 'denied' && initialPermissionState === 'denied') {
                            // Permission was ALREADY blocked - popup never showed
                            if (isAndroid || /Android/i.test(navigator.userAgent)) {
                                showAlert(t.cameraBlockedAndroid || "Camera permission is blocked. To enable:\n1. Tap the three dots (⋮) in browser\n2. Go to Site settings\n3. Tap Camera\n4. Select 'Allow'");
                            } else {
                                showAlert(t.cameraBlockedIOS || "Camera permission is blocked. Please enable it in your browser settings.");
                            }
                        } else {
                            // User just clicked "Block" or "Deny" on the popup
                            showSnackbar(t.cameraDenied || "Camera access was denied", "error");
                        }
                    } catch (permError) {
                        showSnackbar(t.cameraDenied || "Camera access was denied", "error");
                    }
                } else {
                    showSnackbar(t.cameraDenied || "Camera access was denied", "error");

                }
            } else if (err.name === 'NotFoundError') {
                showSnackbar(  'No camera found on this device.' , "error");

            } else if (err.name === 'NotReadableError') {
                showSnackbar(  'Camera is already in use by another application.' , "error");
            }

            return false;
        }
    };

    if (!show) return null;

    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            if (file.type === 'image/gif') {
                resolve(file);
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const img = document.createElement('img');
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const MAX_WIDTH = 1200;
                    const MAX_HEIGHT = 1200;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height = Math.round((height * MAX_WIDTH) / width);
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width = Math.round((width * MAX_HEIGHT) / height);
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                    const quality = 0.7;

                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('Canvas to Blob conversion failed'));
                                return;
                            }
                            const compressedFile = new File([blob], file.name, {
                                type: file.type,
                                lastModified: Date.now(),
                            });
                            resolve(compressedFile);
                        },
                        file.type,
                        quality
                    );
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = event.target.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    // const loadCascade = async (cascadeUrl, filename) => {
    //     try {
    //         const response = await fetch(cascadeUrl);
    //         if (!response.ok) {
    //             throw new Error(`HTTP error! status: ${response.status}`);
    //         }
    //         const xmlData = await response.text();

    //         window.cv.FS_createDataFile('/', filename, xmlData, true, false, false);

    //         return true;
    //     } catch (error) {
    //         console.error(`Error loading cascade ${filename}:`, error);
    //         return false;
    //     }
    // };

    // const detectFaceWithOpenCV = async (imageDataUrl) => {
    //     return new Promise((resolve, reject) => {
    //         const img = document.createElement('img');
    //         img.onload = async () => {
    //             let src, gray, faceCascade;

    //             try {
    //                 const cv = window.cv;

    //                 const canvas = document.createElement('canvas');
    //                 canvas.width = img.width;
    //                 canvas.height = img.height;
    //                 const ctx = canvas.getContext('2d');
    //                 ctx.drawImage(img, 0, 0);

    //                 src = cv.imread(canvas);
    //                 gray = new cv.Mat();

    //                 cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    //                 cv.equalizeHist(gray, gray);

    //                 const cascadeConfig = {
    //                     url: 'https://raw.githubusercontent.com/opencv/opencv/master/data/haarcascades/haarcascade_frontalface_default.xml',
    //                     filename: 'haarcascade_frontalface_default.xml'
    //                 };

    //                 faceCascade = new cv.CascadeClassifier();

    //                 let loaded = false;
    //                 try {
    //                     loaded = faceCascade.load(cascadeConfig.filename);
    //                 } catch (e) {
    //                     console.log('Cascade not in filesystem, loading...');
    //                 }

    //                 if (!loaded) {
    //                     const success = await loadCascade(cascadeConfig.url, cascadeConfig.filename);
    //                     if (success) {
    //                         loaded = faceCascade.load(cascadeConfig.filename);
    //                     }
    //                 }

    //                 if (!loaded) {
    //                     throw new Error('Failed to load face detection model');
    //                 }

    //                 const faces = new cv.RectVector();
    //                 const minSize = new cv.Size(50, 50);

    //                 faceCascade.detectMultiScale(
    //                     gray,
    //                     faces,
    //                     1.2,
    //                     5,
    //                     0,
    //                     minSize
    //                 );

    //                 const numFaces = faces.size();
    //                 console.log(`Detected ${numFaces} face(s) with strict parameters`);

    //                 faces.delete();
    //                 faceCascade.delete();
    //                 src.delete();
    //                 gray.delete();

    //                 if (numFaces > 0) {
    //                     resolve({
    //                         success: true,
    //                         count: numFaces
    //                     });
    //                 } else {
    //                     resolve({
    //                         success: false,
    //                         count: 0,
    //                         error: 'no_face'
    //                     });
    //                 }

    //             } catch (error) {
    //                 console.error('OpenCV detection error:', error);
    //                 if (src) src.delete();
    //                 if (gray) gray.delete();
    //                 if (faceCascade) faceCascade.delete();
    //                 reject(error);
    //             }
    //         };
    //         img.onerror = () => reject(new Error('Failed to load image for face detection'));
    //         img.src = imageDataUrl;
    //     });
    // };

    const handleFileChange = async (e, source) => {
        const selectedFile = e.target.files[0];

        e.target.value = '';

        if (!selectedFile) {
            console.log('No file selected');
            setIsProcessing(false);
            return;
        }

        console.log('File selected:', selectedFile.name, 'from', source);
        onClose();

        if (detect && !opencvReady) {
            setIsProcessing(false);
            if (onError) {
                onError('Face detection is still loading. Please wait a moment and try again.');
            } else {
                alert('Face detection is still loading. Please wait a moment and try again.');
            }
            return;
        }

        try {
            if (onProcessing) {
                onProcessing(true);
            }

            const compressed = await compressImage(selectedFile);

            if (compressed.size > 5 * 1024 * 1024) {
                if (onProcessing) onProcessing(false);
                setIsProcessing(false);
                if (onError) {
                    onError(t.sizeError || 'Image size exceeds 5MB limit');
                } else {
                    alert(t.sizeError || 'Image size exceeds 5MB limit');
                }
                return;
            }

            const reader = new FileReader();
            reader.onloadend = async () => {
                const imageDataUrl = reader.result;

                if (onProcessing) onProcessing(false);
                setIsProcessing(false);
                onImageSelect(imageDataUrl, null);  // Just pass the image directly
            };
            reader.onerror = () => {
                if (onProcessing) onProcessing(false);
                setIsProcessing(false);
                console.error('Error reading file');
                if (onError) {
                    onError('Failed to read image file. Please try again.');
                } else {
                    alert('Failed to read image file. Please try again.');
                }
            };
            reader.readAsDataURL(compressed);
        } catch (error) {
            if (onProcessing) onProcessing(false);
            setIsProcessing(false);
            console.error('Error processing image:', error);
            if (onError) {
                onError('Failed to process image. Please try again.');
            } else {
                alert('Failed to process image. Please try again.');
            }
        }
    };

    const handleCameraClick = async () => {
        if (isProcessing) {
            console.log('Already processing, ignoring click');
            return;
        }

        setIsProcessing(true);

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setIsProcessing(false);
            if (onError) {
                onError(t.cameraNotSupported || 'Camera is not supported on this device.');
            } else {
                alert(t.cameraNotSupported || 'Camera is not supported on this device.');
            }
            return;
        }

        try {
            const permissionGranted = await checkAndRequestCameraPermission();

            if (permissionGranted) {
                console.log('Permission granted, opening camera input...');

                if (cameraInputRef.current) {
                    cameraInputRef.current.click();
                }
            } else {
                setIsProcessing(false);
            }

        } catch (error) {
            console.error('Error handling camera:', error);
            setIsProcessing(false);
            if (onError) {
                onError('Failed to access camera. Please try again.');
            } else {
                alert('Failed to access camera. Please try again.');
            }
        }
    };

    const handleGalleryClick = () => {
        if (isProcessing) {
            console.log('Already processing, ignoring click');
            return;
        }

        setIsProcessing(true);

        if (!deviceCapabilities.hasFileAccess) {
            setIsProcessing(false);
            if (onError) {
                onError(t.galleryNotSupported || 'File access is not supported on this device.');
            } else {
                alert(t.galleryNotSupported || 'File access is not supported on this device.');
            }
            return;
        }

        if (galleryInputRef.current) {
            galleryInputRef.current.click();
        }
    };

    return (
        <>
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                    zIndex: 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(5px)',
                }}
                onClick={onClose}
            >
                <div
                    className="popup-bg rounded-4 p-3 m-3 text-center"
                    style={{
                        maxWidth: '350px',
                        width: '85%',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        transform: isAnimating ? 'scale(1)' : 'scale(0)',
                        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <p className="GTMedium pt-1 popup-main-color" style={{ fontSize: isSmallScreen ? '1.2rem' : '1.3rem', letterSpacing: "-0.3px" }}>
                        {t.popupTitle}
                    </p>

                    <div className="d-flex align-items-center justify-content-around pt-1">
                        <div
                            onClick={handleCameraClick}
                            style={{
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                pointerEvents: isProcessing ? 'none' : 'auto'
                            }}
                        >
                            <img
                                src={camera}
                                alt="camera"
                                width={isSmallScreen ? "55" : "65"}
                                height={isSmallScreen ? "55" : "65"}
                            />
                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => handleFileChange(e, 'camera')}
                                style={{ display: 'none' }}
                            />
                            <p
                                className="pt-2 GTBold m-0 popup-main-color"
                                style={{ fontSize: isSmallScreen ? '0.9rem' : '1rem' }}
                            >
                                {t.camera}
                            </p>
                        </div>

                        <div
                            onClick={handleGalleryClick}
                            style={{
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                pointerEvents: isProcessing ? 'none' : 'auto'
                            }}
                        >
                            <img
                                src={gallery}
                                alt="gallery"
                                width={isSmallScreen ? "55" : "65"}
                                height={isSmallScreen ? "55" : "65"}
                            />
                            <input
                                ref={galleryInputRef}
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, 'gallery')}
                                style={{ display: 'none' }}
                            />
                            <p
                                className="pt-2 GTBold m-0 popup-main-color"
                                style={{ fontSize: isSmallScreen ? '0.9rem' : '1rem' }}
                            >
                                {t.gallery}
                            </p>
                        </div>
                    </div>

                    <p
                        className="my-1 mx-auto popup-border"
                        style={{ width: '90%' }}
                    ></p>
                    <small style={{
                        color: '#ACACAC',
                        fontSize: isSmallScreen ? '0.75rem' : '0.875rem'
                    }}>
                        {t.uploaddes2}
                    </small>

                    <button
                        className="btn px-4 py-2 GTMedium w-100 mt-3 popup-button popup-main-color"
                        style={{
                            border: 'none',
                            borderRadius: '25px',
                            minWidth: '80px',
                            fontSize: isSmallScreen ? '1.1rem' : '1.25rem',
                            padding: isSmallScreen ? '10px 16px' : '12px 16px'
                        }}
                        onClick={onClose}
                    >
                        {t.cancel}
                    </button>
                </div>
            </div>

            {/* Camera Permission Alert Modal */}
            {alertModal.show && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        zIndex: 9999,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        backdropFilter: 'blur(8px)'
                    }}
                    onClick={() => setAlertModal({ show: false })}
                >
                    <div
                        className="popup-bg rounded-4 p-3 m-3"
                        style={{
                            maxWidth: '420px',
                            width: '90%',
                            boxShadow: '0 20px 55px rgba(0, 0, 0, 0.3)',
                            maxHeight: '90vh',
                            overflowY: 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Content */}
                        <div
                            className="text-center rounded-4 p-2 mx-auto"
                            style={{
                                maxWidth: "360px",
                                // border: "1px solid #e9ecef"
                            }}
                        >
                            {/* Icon */}
                            <div
                                className="d-flex align-items-center justify-content-center mx-auto mb-2"
                                style={{
                                    width: "50px",
                                    height: "50px",
                                    borderRadius: "50%",
                                    background: "#FFECEC",
                                    fontSize: "22px"
                                }}
                            >
                                📷
                            </div>

                            {/* Title */}
                            <p className="mb-2 fw-semibold popup-main-color" style={{ fontSize: "18px" }}>
                                {t.cameraPermissionTitle || "Camera Permission Required"}
                            </p>

                            {/* Description */}
                            <p className="mb-3" style={{ fontSize: "13px", lineHeight: "1.3", color: "#A7A7A7" }}>
                                {t.cameraPermissionDesc || "This app needs camera permission to take photos."}
                            </p>

                            {/* Steps */}
                            <div
                                className="text-start popup-center-bg d-flex flex-column justify-content-around align-items-center popup-border-dashed"
                                style={{
                                    fontSize: "16px",
                                    lineHeight: "1.7",
                                    borderRadius: "12px",
                                    padding: "12px 12px 13px 12px",
                                }}
                            >
                                <h6 className="GTBold text-center popup-main-color">{t.steps} </h6>
                                <ol className="m-0 my-4 popup-main-color">
                                    <li className="mb-2">{t.cameraStep1?.text || "Tap the three dots"} <b>{t.cameraStep1?.bold || "(⋮)"}</b> {t.cameraStep1?.text2 || "in browser"}</li>
                                    <li className="mb-2">{t.cameraStep2?.text || "Go to"} <b>{t.cameraStep2?.bold || "Site settings"}</b></li>
                                    <li className="mb-2">{t.cameraStep3?.text || "Tap"} <b>{t.cameraStep3?.bold || "Camera"}</b></li>
                                    <li className="">{t.cameraStep4?.text || "Select"} <b>{t.cameraStep4?.bold || "'Allow'"}</b></li>
                                </ol>
                            </div>
                        </div>

                        {/* Button */}
                        <div className="text-center mt-3">
                            <button
                                className="btn fw-semibold"
                                style={{
                                    width: "90%",
                                    backgroundColor: '#F55152',
                                    color: 'white',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    padding: '10.px'
                                }}
                                onClick={() => setAlertModal({ show: false })}
                            >
                                {t.close || "Close"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default UploadModal;